/**
 * 路由配置（17 条，含 RequireAuth 守卫与 404 回退）
 * 对齐架构 §4 页面路由表
 */
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '@/stores/auth';
import AppLayout from '@/components/layout/AppLayout';

import Hall from '@/pages/hall/Hall';
import Market from '@/pages/market/Market';
import CattleDetail from '@/pages/cattle/CattleDetail';
import AuctionHall from '@/pages/auction/AuctionHall';
import AuctionDetail from '@/pages/auction/AuctionDetail';
import Quote from '@/pages/quote/Quote';
import Login from '@/pages/auth/Login';
import Register from '@/pages/auth/Register';
import Checkout from '@/pages/checkout/Checkout';
import ListingNew from '@/pages/listing/ListingNew';
import MyListings from '@/pages/listing/MyListings';
import OrderList from '@/pages/order/OrderList';
import OrderDetail from '@/pages/order/OrderDetail';
import BrokerOrderNew from '@/pages/broker/BrokerOrderNew';
import BrokerOrders from '@/pages/broker/BrokerOrders';
import BrokerWorkbench from '@/pages/broker/BrokerWorkbench';
import UserCenter from '@/pages/user/UserCenter';
import Wallet from '@/pages/user/Wallet';
import EnterpriseApply from '@/pages/user/EnterpriseApply';

/** 登录守卫：未登录跳 /login?redirect=原路径（保留操作意图） */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) {
    const redirect = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }
  return <>{children}</>;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route element={<AppLayout />}>
        {/* 公开浏览 */}
        <Route path="/" element={<Hall />} />
        <Route path="/market" element={<Market />} />
        <Route path="/cattle/:id" element={<CattleDetail />} />
        <Route path="/auctions" element={<AuctionHall />} />
        <Route path="/auction/:id" element={<AuctionDetail />} />
        <Route path="/quotes" element={<Quote />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />

        {/* 需登录 */}
        <Route
          path="/checkout"
          element={
            <RequireAuth>
              <Checkout />
            </RequireAuth>
          }
        />
        <Route
          path="/listing/new"
          element={
            <RequireAuth>
              <ListingNew />
            </RequireAuth>
          }
        />
        <Route
          path="/my/listings"
          element={
            <RequireAuth>
              <MyListings />
            </RequireAuth>
          }
        />
        <Route
          path="/orders"
          element={
            <RequireAuth>
              <OrderList />
            </RequireAuth>
          }
        />
        <Route
          path="/orders/:id"
          element={
            <RequireAuth>
              <OrderDetail />
            </RequireAuth>
          }
        />
        <Route
          path="/broker/orders/new"
          element={
            <RequireAuth>
              <BrokerOrderNew />
            </RequireAuth>
          }
        />
        <Route
          path="/broker/orders"
          element={
            <RequireAuth>
              <BrokerOrders />
            </RequireAuth>
          }
        />
        <Route
          path="/broker/workbench"
          element={
            <RequireAuth>
              <BrokerWorkbench />
            </RequireAuth>
          }
        />
        <Route
          path="/my"
          element={
            <RequireAuth>
              <UserCenter />
            </RequireAuth>
          }
        />
        <Route
          path="/my/wallet"
          element={
            <RequireAuth>
              <Wallet />
            </RequireAuth>
          }
        />
        <Route
          path="/my/enterprise"
          element={
            <RequireAuth>
              <EnterpriseApply />
            </RequireAuth>
          }
        />

        {/* 404 回首页 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
